import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, WMSTileLayer, Polygon, Polyline, Marker, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import { FaTimes, FaDrawPolygon, FaListUl } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'leaflet/dist/leaflet.css';
import {
  areAdjacent, builtShare, containsPoint, contiguousGroups, imperviousShare, LASSO_MIN_SHARE, parcelHint,
  parseParcelList, plotSurfaceCheck, selectionTotals, shareInsideLasso, toLatLngs,
} from './siteParcels';

// Fenêtre « Terrain du site » : l'auditeur compose le terrain parcelle par parcelle (clic, lasso
// ou liste collée) sur fond de photo aérienne, voit le CBS de la sélection se mettre à jour
// (/cbs_parcel, rien n'est enregistré), puis valide : `onValidate(identifiants)` enregistre
// (CbsPanel → /compute_cbs du bâtiment ; outil « CBS d'un site » → /cbs_sites) et renvoie vrai
// pour fermer. Les contours viennent directement du WFS cadastre de l'IGN.

const WFS_URL = 'https://data.geopf.fr/wfs/ows';
const PARCELS_MIN_ZOOM = 17;
const MAX_PARALLEL_COMPUTATIONS = 2; // l'API calcule une parcelle en ~0,4 s ; ne pas saturer ses workers
const IGN_ATTRIBUTION = '© IGN, cadastre DGFiP';

const wmts = (layer, format, style = 'normal') =>
  `https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=${layer}&STYLE=${encodeURIComponent(style)}`
  + `&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&FORMAT=${format}`;

const BASEMAPS = {
  ortho: 'Photo aérienne',
  plan: 'Plan cadastral',
  cosia: 'Occupation du sol (CoSIA)',
};

const formatNumber = (value, digits = 0) =>
  Number(value ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const percent = (value) => `${Math.round((value || 0) * 100)} %`;
const shortIdu = (idu) => `${idu.slice(8, 10).replace(/^0/, '')} ${Number(idu.slice(10))}`;

async function fetchParcelsInView(bounds, signal) {
  const bbox = [bounds.getSouth(), bounds.getWest(), bounds.getNorth(), bounds.getEast(), 'urn:ogc:def:crs:EPSG::4326'].join(',');
  const params = new URLSearchParams({
    SERVICE: 'WFS', VERSION: '2.0.0', REQUEST: 'GetFeature', TYPENAMES: 'CADASTRALPARCELS.PARCELLAIRE_EXPRESS:parcelle',
    OUTPUTFORMAT: 'application/json', SRSNAME: 'EPSG:4326', BBOX: bbox, COUNT: '1000',
  });
  const response = await fetch(`${WFS_URL}?${params}`, { signal });
  if (!response.ok) throw new Error(`cadastre IGN : ${response.status}`);
  const data = await response.json();
  return (data.features || []).map((feature) => ({
    idu: feature.properties.idu,
    contenance: feature.properties.contenance,
    geometry: feature.geometry,
  }));
}

// Recharge les parcelles visibles à chaque déplacement de la carte (à partir du zoom 17).
function ParcelLoader({ onParcels, onZoom }) {
  const abortRef = useRef(null);
  const map = useMap();
  const load = useCallback(() => {
    onZoom(map.getZoom());
    if (map.getZoom() < PARCELS_MIN_ZOOM) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    fetchParcelsInView(map.getBounds(), controller.signal)
      .then(onParcels)
      .catch((error) => { if (error.name !== 'AbortError') toast.error(`Parcelles indisponibles (${error.message}).`); });
  }, [map, onParcels, onZoom]);
  useMapEvents({ moveend: load });
  useEffect(() => { load(); return () => abortRef.current?.abort(); }, [load]);
  return null;
}

function FitTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.fitBounds(target, { padding: [40, 40], maxZoom: 19 });
  }, [map, target]);
  return null;
}

// Lasso : glisser sur la carte trace un contour ; au relâchement, les parcelles majoritairement
// dedans rejoignent la sélection. La carte ne se déplace plus tant que le lasso est actif.
function Lasso({ active, onDone }) {
  const map = useMap();
  const [points, setPoints] = useState([]);
  const drawing = useRef(false);
  useEffect(() => {
    if (active) map.dragging.disable(); else map.dragging.enable();
    map.getContainer().style.cursor = active ? 'crosshair' : '';
    return () => { map.dragging.enable(); map.getContainer().style.cursor = ''; };
  }, [active, map]);
  useMapEvents({
    mousedown: (e) => { if (active) { drawing.current = true; setPoints([e.latlng]); } },
    mousemove: (e) => { if (active && drawing.current) setPoints((prev) => [...prev, e.latlng]); },
    mouseup: () => {
      if (!active || !drawing.current) return;
      drawing.current = false;
      if (points.length > 2) onDone(points.map((p) => [p.lng, p.lat]));
      setPoints([]);
    },
  });
  return points.length > 1 ? <Polyline positions={points} pathOptions={{ color: '#ffd400', weight: 2, dashArray: '4 4' }} /> : null;
}

export default function SiteParcelsModal({ title = 'Terrain du site', latitude, longitude, initialParcels, plotSurface, onClose, onValidate, onUsePlotSurface }) {
  const [basemap, setBasemap] = useState('ortho');
  const [zoom, setZoom] = useState(18);
  const [parcels, setParcels] = useState(() => new Map((initialParcels || []).map((p) => [p.idu, { idu: p.idu, contenance: p.contenance_m2, geometry: p.geometry }])));
  const [selected, setSelected] = useState(() => (initialParcels || []).map((p) => p.idu));
  const [results, setResults] = useState({});
  const [lassoActive, setLassoActive] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [fitTarget, setFitTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const queue = useRef([]);
  const running = useRef(0);
  const requested = useRef(new Set());
  const autoSelected = useRef(selected.length > 0);

  // Mémorisé : une nouvelle référence à chaque rendu relancerait le chargement du cadastre.
  const point = useMemo(() => {
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    return Number.isFinite(lat) && Number.isFinite(lon) ? [lat, lon] : null;
  }, [latitude, longitude]);

  // --- Calcul des parcelles retenues, au plus MAX_PARALLEL_COMPUTATIONS à la fois ---------------
  const pump = useCallback(() => {
    while (running.current < MAX_PARALLEL_COMPUTATIONS && queue.current.length) {
      const idu = queue.current.shift();
      running.current += 1;
      fetch(`${process.env.REACT_APP_API_URL}/cbs_parcel/${idu}`)
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.error || `erreur ${response.status}`);
          setResults((prev) => ({ ...prev, [idu]: { status: 'ok', data } }));
          setParcels((prev) => (prev.get(idu)?.geometry ? prev : new Map(prev).set(idu, { idu, contenance: data.contenance_m2, geometry: data.geometry })));
        })
        .catch((error) => {
          requested.current.delete(idu);
          setResults((prev) => ({ ...prev, [idu]: { status: 'error', error: error.message } }));
        })
        .finally(() => { running.current -= 1; pump(); });
    }
  }, []);

  const request = useCallback((idus) => {
    idus.filter((idu) => !requested.current.has(idu)).forEach((idu) => {
      requested.current.add(idu);
      queue.current.push(idu);
      setResults((prev) => ({ ...prev, [idu]: { status: 'loading' } }));
    });
    pump();
  }, [pump]);

  useEffect(() => { request(selected); }, [selected, request]);

  // Vue de départ : les parcelles déjà retenues, sinon le point du bâtiment.
  useEffect(() => {
    const rings = (initialParcels || []).flatMap((p) => toLatLngs(p.geometry)).flat();
    if (rings.length) setFitTarget(rings);
  }, [initialParcels]);

  const addParcels = useCallback((list) => {
    setParcels((prev) => {
      const next = new Map(prev);
      list.forEach((p) => { if (!next.get(p.idu)?.geometry) next.set(p.idu, p); });
      return next;
    });
  }, []);

  const onParcelsLoaded = useCallback((list) => {
    addParcels(list);
    // Sans sélection, on part de la parcelle sous le point du bâtiment.
    if (!autoSelected.current && point) {
      const under = list.find((p) => containsPoint(p.geometry, [point[1], point[0]]));
      if (under) {
        autoSelected.current = true;
        setSelected((prev) => (prev.length ? prev : [under.idu]));
      }
    }
  }, [addParcels, point]);

  const toggle = (idu) => setSelected((prev) => (prev.includes(idu) ? prev.filter((id) => id !== idu) : [...prev, idu]));
  const remove = (idu) => setSelected((prev) => prev.filter((id) => id !== idu));

  const onLasso = (ring) => {
    const inside = [...parcels.values()].filter((p) => shareInsideLasso(p.geometry, ring) > LASSO_MIN_SHARE).map((p) => p.idu);
    const added = inside.filter((idu) => !selected.includes(idu));
    setSelected((prev) => [...prev, ...added]);
    setLassoActive(false);
    toast.info(added.length ? `${added.length} parcelle(s) ajoutée(s).` : 'Aucune nouvelle parcelle dans le lasso.');
  };

  const onPaste = () => {
    const known = selected[0] || [...parcels.keys()][0];
    const { ids, unparsed } = parseParcelList(pasteText, known ? known.slice(0, 8) : null);
    if (unparsed.length) toast.warning(`Non reconnu : ${unparsed.join(', ')}${known ? '' : ' (donner l’identifiant complet, ex. 64102000AK0730)'}`);
    if (!ids.length) return;
    setSelected((prev) => [...prev, ...ids.filter((idu) => !prev.includes(idu))]);
    setPasteText('');
    setPasteOpen(false);
  };

  // --- Dérivés de la sélection --------------------------------------------------------------
  const selectedParcels = useMemo(() => selected.map((idu) => parcels.get(idu)).filter((p) => p?.geometry), [selected, parcels]);
  const candidates = useMemo(() => new Set(
    [...parcels.values()]
      .filter((p) => !selected.includes(p.idu) && selectedParcels.some((s) => areAdjacent(s.geometry, p.geometry)))
      .map((p) => p.idu)
  ), [parcels, selected, selectedParcels]);
  const groups = useMemo(() => contiguousGroups(selectedParcels), [selectedParcels]);
  const totals = selectionTotals(selected.map((idu) => (results[idu]?.status === 'ok' ? results[idu].data : null)));
  const errors = selected.filter((idu) => results[idu]?.status === 'error');
  // Comparaison à la surface de parcelle du bâtiment : seulement depuis sa fiche.
  const surfaceCheck = onUsePlotSurface ? plotSurfaceCheck(totals.pending ? 0 : totals.surface, plotSurface) : { state: 'none' };

  useEffect(() => {
    if (selected.length && selectedParcels.length === selected.length && !fitTarget) {
      setFitTarget(selectedParcels.flatMap((p) => toLatLngs(p.geometry)).flat());
    }
  }, [selected, selectedParcels, fitTarget]);

  const validate = async () => {
    setSaving(true);
    try {
      if (await onValidate(selected)) onClose();
    } finally {
      setSaving(false);
    }
  };

  const style = (idu) => {
    if (selected.includes(idu)) return { color: '#00e5ff', weight: 3, fillColor: '#00e5ff', fillOpacity: 0.3 };
    if (candidates.has(idu)) return { color: '#ff9f1c', weight: 2, dashArray: '6 4', fillOpacity: 0.05 };
    return { color: basemap === 'plan' ? '#555' : '#ffffff', weight: 1, fillOpacity: 0 };
  };

  return (
    <div className="site-parcels-overlay">
      <div className="site-parcels-modal">
        <header className="site-parcels-header">
          <div>
            <h2>{title}</h2>
            <p>Cliquez sur les parcelles pour les ajouter ou les retirer. En pointillé orange : les parcelles voisines de la sélection.</p>
          </div>
          <button type="button" className="site-parcels-close" onClick={onClose} aria-label="Fermer"><FaTimes /></button>
        </header>

        <div className="site-parcels-body">
          <div className="site-parcels-map">
            <div className="site-parcels-toolbar">
              {Object.entries(BASEMAPS).map(([key, label]) => (
                <button key={key} type="button" className={basemap === key ? 'active' : ''} onClick={() => setBasemap(key)}>{label}</button>
              ))}
              <span className="site-parcels-toolbar-gap" />
              <button type="button" className={lassoActive ? 'active' : ''} onClick={() => setLassoActive((v) => !v)} title="Entourer le site pour retenir toutes les parcelles majoritairement dedans">
                <FaDrawPolygon /> {lassoActive ? 'Tracez autour du site…' : 'Lasso'}
              </button>
              <button type="button" className={pasteOpen ? 'active' : ''} onClick={() => setPasteOpen((v) => !v)}><FaListUl /> Coller une liste</button>
            </div>
            {pasteOpen && (
              <div className="site-parcels-paste">
                <textarea
                  rows={3}
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder={'Identifiants ou références cadastrales, ex. :\n64102000AK0730, AK 731, AK 732'}
                />
                <button type="button" className="small-black-button" onClick={onPaste} disabled={!pasteText.trim()}>Ajouter</button>
              </div>
            )}
            {zoom < PARCELS_MIN_ZOOM && <div className="site-parcels-zoom-hint">Zoomez pour afficher les parcelles</div>}
            <MapContainer center={point || [46.6, 1.9]} zoom={point ? 18 : 6} maxZoom={20} className="site-parcels-leaflet">
              {basemap === 'plan' ? (
                <>
                  <TileLayer url={wmts('GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2', 'image/png')} maxNativeZoom={19} maxZoom={20} attribution={IGN_ATTRIBUTION} />
                  <TileLayer url={wmts('CADASTRALPARCELS.PARCELLAIRE_EXPRESS', 'image/png', 'PCI vecteur')} maxNativeZoom={19} maxZoom={20} />
                </>
              ) : (
                <TileLayer url={wmts('ORTHOIMAGERY.ORTHOPHOTOS', 'image/jpeg')} maxNativeZoom={19} maxZoom={20} attribution={IGN_ATTRIBUTION} />
              )}
              {basemap === 'cosia' && (
                <WMSTileLayer url="https://data.geopf.fr/wms-r/wms" layers="IGNF_COSIA_2021-2023,IGNF_COSIA_2024-2026" format="image/png" transparent opacity={0.85} maxZoom={20} />
              )}
              <ParcelLoader onParcels={onParcelsLoaded} onZoom={setZoom} />
              <FitTo target={fitTarget} />
              <Lasso active={lassoActive} onDone={onLasso} />
              {[...parcels.values()].filter((p) => p.geometry).map((p) => (
                <Polygon
                  key={p.idu}
                  positions={toLatLngs(p.geometry)}
                  pathOptions={style(p.idu)}
                  eventHandlers={{ click: () => { if (!lassoActive) toggle(p.idu); } }}
                >
                  <Tooltip sticky>
                    <strong>{shortIdu(p.idu)}</strong> ({p.idu})<br />
                    {formatNumber(p.contenance)} m² au cadastre
                    {results[p.idu]?.status === 'ok' && <><br />CBS {formatNumber(results[p.idu].data.cbs, 2)}</>}
                  </Tooltip>
                </Polygon>
              ))}
              {point && <Marker position={point} />}
            </MapContainer>
          </div>

          <aside className="site-parcels-side">
            <div className="site-parcels-totals">
              <div><span>Parcelles</span><strong>{selected.length}</strong></div>
              <div><span>Surface</span><strong>{formatNumber(totals.surface)} m²</strong></div>
              <div><span>CBS</span><strong>{totals.cbs == null ? '–' : formatNumber(totals.cbs, 2)}</strong></div>
              <div><span>CBS potentiel</span><strong>{totals.cbsPotential == null ? '–' : formatNumber(totals.cbsPotential, 2)}</strong></div>
            </div>
            {totals.pending > 0 && <p className="site-parcels-note">Calcul de {totals.pending} parcelle(s) en cours…</p>}

            {groups.length > 1 && (
              <p className="site-parcels-warning">
                Terrain en {groups.length} morceaux non contigus ({groups.map((g) => g.map(shortIdu).join(', ')).join(' | ')}) : à vérifier (parking de l’autre côté de la rue ?).
              </p>
            )}
            {surfaceCheck.state === 'empty' && (
              <p className="site-parcels-note">
                Surface de parcelle non renseignée dans le bâtiment.{' '}
                <button type="button" className="site-parcels-link" onClick={() => onUsePlotSurface(Math.round(totals.surface))}>La remplir avec {formatNumber(totals.surface)} m²</button>
              </p>
            )}
            {surfaceCheck.state === 'different' && (
              <p className="site-parcels-warning">
                Surface de parcelle saisie : {formatNumber(surfaceCheck.entered)} m², soit {surfaceCheck.gap > 0 ? '+' : ''}{percent(surfaceCheck.gap)} d’écart avec le terrain retenu.{' '}
                <button type="button" className="site-parcels-link" onClick={() => onUsePlotSurface(Math.round(totals.surface))}>Remplacer par {formatNumber(totals.surface)} m²</button>
              </p>
            )}

            <ul className="site-parcels-list">
              {selected.length === 0 && <li className="site-parcels-note">Aucune parcelle retenue.</li>}
              {selected.map((idu) => {
                const result = results[idu];
                const data = result?.status === 'ok' ? result.data : null;
                const hint = parcelHint(data);
                return (
                  <li key={idu} className={hint || result?.status === 'error' ? 'flagged' : ''}>
                    <div className="site-parcels-item-head">
                      <strong title={idu}>{shortIdu(idu)}</strong>
                      <span className="site-parcels-idu">{idu.slice(0, 5)}</span>
                      <button type="button" onClick={() => remove(idu)} aria-label={`Retirer ${idu}`}><FaTimes /></button>
                    </div>
                    {result?.status === 'loading' && <span className="site-parcels-note">calcul…</span>}
                    {result?.status === 'error' && <span className="site-parcels-error">{result.error}</span>}
                    {data && (
                      <span className="site-parcels-item-figures">
                        {formatNumber(data.surface_m2)} m² · CBS {formatNumber(data.cbs, 2)} · bâti {percent(builtShare(data))} · imperméable {percent(imperviousShare(data))}
                      </span>
                    )}
                    {hint && <span className="site-parcels-hint">{hint}</span>}
                  </li>
                );
              })}
            </ul>

            <footer className="site-parcels-footer">
              {errors.length > 0 && <p className="site-parcels-error">Retirez les parcelles en erreur pour valider.</p>}
              <button type="button" className="site-parcels-cancel" onClick={onClose}>Annuler</button>
              <button type="button" className="small-black-button" onClick={validate}
                disabled={saving || !selected.length || totals.pending > 0 || errors.length > 0}>
                {saving ? 'Enregistrement…' : `Valider le terrain (${selected.length})`}
              </button>
            </footer>
          </aside>
        </div>
      </div>
    </div>
  );
}
